/**
 * RealtimeContext - React integration for SSE real-time events
 * Provides hooks for subscribing to events and connection state
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { realtimeClient, ConnectionState, EventType } from '../services/realtime'
import { useAuth } from './AuthContext'

const RealtimeContext = createContext(null)

export function RealtimeProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED)

  // Connect/disconnect based on auth state
  useEffect(() => {
    if (isAuthenticated) {
      realtimeClient.connect()
    } else {
      realtimeClient.disconnect()
    }

    return () => {
      realtimeClient.disconnect()
    }
  }, [isAuthenticated])

  // Listen for connection state changes
  useEffect(() => {
    const unsubscribe = realtimeClient.onStateChange(setConnectionState)
    return unsubscribe
  }, [])

  // Manual reconnect function
  const reconnect = useCallback(() => {
    realtimeClient.disconnect()
    realtimeClient.connect()
  }, [])

  const value = {
    connectionState,
    isConnected: connectionState === ConnectionState.CONNECTED,
    isConnecting: connectionState === ConnectionState.CONNECTING,
    hasError: connectionState === ConnectionState.ERROR,
    reconnect,
    // Expose the client for direct subscription
    subscribe: realtimeClient.subscribe.bind(realtimeClient),
  }

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  )
}

/**
 * Hook to access realtime context
 */
export function useRealtime() {
  const context = useContext(RealtimeContext)
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider')
  }
  return context
}

/**
 * Hook to subscribe to a specific event type
 * @param {string} eventType - Event type from EventType enum
 * @param {Function} callback - Callback function receiving event payload
 */
export function useRealtimeEvent(eventType, callback) {
  const { subscribe } = useRealtime()

  useEffect(() => {
    const unsubscribe = subscribe(eventType, callback)
    return unsubscribe
  }, [eventType, callback, subscribe])
}

/**
 * Hook to subscribe to multiple event types
 * @param {string[]} eventTypes - Array of event types
 * @param {Function} callback - Callback receiving (eventType, payload)
 */
export function useRealtimeEvents(eventTypes, callback) {
  const { subscribe } = useRealtime()

  useEffect(() => {
    const unsubscribes = eventTypes.map((eventType) =>
      subscribe(eventType, (payload) => callback(eventType, payload))
    )

    return () => {
      unsubscribes.forEach((unsub) => unsub())
    }
  }, [eventTypes, callback, subscribe])
}

// Re-export for convenience
export { ConnectionState, EventType }

export default RealtimeContext
